import { BaseService } from '../../../http/BaseHttpService';
import {
    GetChatMessagesRequest,
    IChatMessagesService,
    SendChatMessageResponse,
    SendNexusChatMessageRequest,
} from '../../../chat/interfaces/IChatMessagesService';

import { NexusMessageDto } from '../dtos/responses/NexusMessageDto';
import {
    NexusSendMessageResponseDto,
    NexusSendMessageResponseMessageDto,
} from '../dtos/responses/NexusSendMessageResponseDto';


import { mapNexusMessageDtoToModel, mapToChatCitation } from '../mappers/NexusMessageMapper';
import { buildNexusUrl } from '../../../../features/utils/buildNexusUrl';
import { ChatMessage } from '../../../../domain/messages/models/ChatMessage';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';

export class NexusConversationMessagesService extends BaseService implements IChatMessagesService {
    public readonly providerType = ChatProviderType.Nexus;
    private readonly subscriptionKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY ?? '';
    private readonly subKeyHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME ?? '';

    constructor(private readonly baseUrl: string) {
        super();
    }


    public async getChatMessagesAsync(request: GetChatMessagesRequest, accessToken: string): Promise<ChatMessage[]> {
        const searchParams = new URLSearchParams();

        if (request.skip !== undefined) {
            searchParams.set('skip', request.skip.toString());
        }

        if (request.count !== undefined) {
            searchParams.set('count', request.count.toString());
        }

        const queryString = searchParams.toString();
        const url = buildNexusUrl(
            this.baseUrl,
            `conversations/${request.chatId}/messages${queryString ? `?${queryString}` : ''}`,
        );

        const response = await this.getResponseAsync<NexusMessageDto[]>(
            {
                url,
                method: 'GET',
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },

            accessToken,
        );
        return response.map((message) => mapNexusMessageDtoToModel(message, request.chatId, this.baseUrl));

    }

    public async sendMessageAsync(
        request: SendNexusChatMessageRequest,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<SendChatMessageResponse> {

        if (!request.agentId || typeof request.agentId !== 'string') {
            throw new Error('agentId is required to send a Nexus conversation message.');
        }

        const url = buildNexusUrl(
            this.baseUrl,
            `conversations/${encodeURIComponent(request.chatId)}/invoke`,
        );

        const formData = new FormData();
        formData.append('AgentId', request.agentId);
        formData.append('Message', request.input);
        request.images?.forEach(file => formData.append('images', file));

        const response = await this.getResponseAsync<NexusSendMessageResponseDto>(
            {
                url,
                method: 'POST',
                body: formData,
                headers: {
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
            signal,
        );

        const lastMessage = response.messages[response.messages.length - 1];

        if (!lastMessage) {
            throw new Error('Nexus agent response did not contain any messages.');
        }
        return {
            status: 'streaming',
            chatId: request.chatId,
            agentId: request.agentId,
            message: this.getSendMessageContent(lastMessage),
            citations: mapToChatCitation(response.citations),
        };
    }

    public async savePartialMessageAsync(chatId: string, content: string, accessToken: string, role: string = 'assistant'): Promise<void> {
        const url = buildNexusUrl(this.baseUrl, `conversations/${encodeURIComponent(chatId)}/messages`);

        await this.getResponseAsync(
            {
                url,
                method: 'POST',
                body: { Content: content, Role: role },
                headers: {
                    'Content-Type': 'application/json',
                    [this.subKeyHeader]: this.subscriptionKey,
                },
            },
            accessToken,
        );
    }

    private getSendMessageContent(message: NexusSendMessageResponseMessageDto): string {
        return message.content ?? message.text ?? '';
    }
}

